import React from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
// lodash
import _ from 'lodash';
// mui
import {
  Box,
  Card as MuiCard,
  Divider,
  styled,
  Backdrop,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { CurrencyAmount } from '@cowbell/ui/core';

// components
import CbButton from '../../../components/Buttons/CbButton';
import { P100QuoteDrawer } from './drawers/P100QuoteDetails';
import { P250QuoteDrawer } from './drawers/P250QuoteDrawer';
// actions
import { fetchRenewalPolicyId, getQuote } from '../../../inbox/QuotesService';
import { getPolicyDetails } from '../../../policies/PolicyService';
// helpers
import { Modal } from '../../../components/modals/v2/helpers/v2.modal.helpers';
import {
  formatPolicy,
  handleSignatureInitiation,
} from './customerRenewalsUtils';
import { ProductTypes } from '../../../types';
import { RenewalWorkflowEmailAgencyConfig } from '../../../components/modals/v2/RenewalWorkflowEmailAgency';
import useNotifications from '../../../_lib/notifications/useNotifications';
import { useAPIErrorHandler } from '../../../components/hooks/useAPIErrorHandler';

export const RenewalSummary = ({ product, data = {}, ...props }: any) => {
  const { formattedQuote: quote } = data;
  // @ts-expect-error TS(2339): Property 'quoteId' does not exist on type '{}'.
  const { quoteId } = useParams();
  const { push } = useHistory();
  const notifications = useNotifications();
  const handleAPIError = useAPIErrorHandler();

  const [drawerToggle, setDrawerToggle] = React.useState(false);
  // use effect and the bottom two state are used because there is a delay in recieving total premium from boost.
  const [manualLoad, setLoading] = React.useState(false);
  const [totalPremium, setTotalPremium] = React.useState(0);

  // cannot invalidate/refetch apis from the query client from the parent workflow as the data is not refreshed on this page in time and in turn results in wrong premium
  React.useEffect(() => {
    if (product === ProductTypes.p100) {
      setLoading(true);
      const quoteDetailsInterval = setInterval(() => {
        getQuote(quoteId, ProductTypes.p100).then((resp) => {
          if (!resp?.data?.isUpdating) {
            setTotalPremium(resp?.data?.totalPremium);
            setLoading(false);
            clearInterval(quoteDetailsInterval);
          }
        });
      }, 6000);
      return () => {
        clearInterval(quoteDetailsInterval);
      };
    }

    // eslint-disable-next-line
  }, []);

  const { data: policy = {}, isLoading } = useQuery(
    ['renewal-policyId'],
    () => {
      return fetchRenewalPolicyId({ quoteId })
        .then((resp: any) => {
          // @ts-expect-error TS(2554): Expected 3 arguments, but got 2.
          return getPolicyDetails(resp.data, product)
            .then((response) => {
              return formatPolicy(response.data, product);
            })
            .catch(() => {
              notifications.error(
                'Renewal Summary not available at this time.'
              );
              push('/customer/policies');
            });
        })
        .catch(
          handleAPIError('Attestation not available as its not a renewal quote')
        );
    },
    { refetchOnWindowFocus: false }
  );

  const handleModal = () => {
    Modal.show(RenewalWorkflowEmailAgencyConfig, { data: quote });
  };

  const handleSubmit = () => {
    if (_.isFunction(props.onSubmit)) {
      props.onSubmit();
    } else {
      handleSignatureInitiation(quote, push);
    }
  };

  const handleCancel = () => {
    if (_.isFunction(props.onCancel)) {
      props.onCancel();
    } else {
      push('/customer/policies');
    }
  };

  if (isLoading || manualLoad) {
    return <LoadingRenewalSummary />;
  }

  let QuoteDrawer;
  switch (product) {
    case ProductTypes.p100:
      QuoteDrawer = P100QuoteDrawer;
      break;
    case ProductTypes.p250:
      QuoteDrawer = P250QuoteDrawer;
      break;
    default:
      QuoteDrawer = React.Fragment;
      push('/customer/policies');
  }

  return (
    <>
      <QuoteDrawer
        quote={quote}
        toggle={drawerToggle}
        setToggle={setDrawerToggle}
      />

      <MuiCard>
        <Container pl={3} pr={3} pb={3}>
          <Box
            mt={4}
            mb={1}
            display="flex"
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <HeaderTitle>{quote.name}</HeaderTitle>
            <CbButton
              onClick={() => setDrawerToggle((current) => !current)}
              styleName="primary"
            >
              View Details
            </CbButton>
          </Box>

          <Divider style={{ margin: '1.5rem 0' }} />

          <Box display="flex" width="100%" style={{ margin: '0 auto' }}>
            {[' ', 'Current', 'Renewal'].map((el) => (
              <HeaderRow
                key={el}
                display="flex"
                justifyContent="flex-end"
                width="33%"
              >
                {/* @ts-expect-error: FIXME */}
                <HeaderTitle isBold={false}>{el}</HeaderTitle>
              </HeaderRow>
            ))}
          </Box>

          <Box display="flex" width="100%">
            <Box display="flex" flexDirection="column" width="33.33%">
              {['Limit', 'Deductible', 'Premium', 'Policy Effective'].map(
                (el) => (
                  <Row key={el}>
                    <TableText>{el}</TableText>
                  </Row>
                )
              )}
            </Box>

            <Box display="flex" flexDirection="column" width="33.33%">
              {[
                policy.formattedLimit,
                policy.formattedDeductible,
                policy.formattedTotalPremium,
                policy.formattedEffectiveDate,
              ].map((el) => (
                <Row justifyContent="flex-end" key={el}>
                  {/* @ts-expect-error: FIXME */}
                  <TableText isFaded>{el}</TableText>
                </Row>
              ))}
            </Box>

            {(product === ProductTypes.p100 || product === ProductTypes.p250) && (
              <Box display="flex" flexDirection="column" width="33.33%">
                {[
                  <CurrencyAmount
                    amount={quote.limit}
                    currency={quote.currency}
                    minimumFractionDigits={0}
                  />,
                  <CurrencyAmount
                    amount={quote.deductible}
                    currency={quote.currency}
                    minimumFractionDigits={0}
                  />,
                  <CurrencyAmount
                    // P100's live premium can lag boost, so it's polled separately (see
                    // the effect above) rather than trusted from the initial quote fetch.
                    amount={
                      product === ProductTypes.p100 ? totalPremium : quote.totalPremium
                    }
                    currency={quote.currency}
                  />,
                  quote.formattedEffectiveDate,
                ].map((el, index) => (
                  <Row justifyContent="flex-end" key={index}>
                    <TableText>{el}</TableText>
                  </Row>
                ))}
              </Box>
            )}
          </Box>

          <Box display="flex" justifyContent="center" alignItems="center">
            <MailOutlineIcon
              color="primary"
              style={{ marginRight: '0.25rem' }}
            />
            {/* @ts-expect-error: FIXME */}
            <Text fontSize="tertia">
              To make changes to limit(s), deductible(s) and coverage(s), please
              contact your agent,{' '}
              <Anchor onClick={handleModal}>click here</Anchor>
            </Text>
          </Box>

          <Divider style={{ margin: '0.25rem 0 1.25rem 0' }} />

          <Box display="flex" justifyContent="center" alignItems="center">
            <CbButton
              styleName="cancel"
              onClick={handleCancel}
              buttonText="Cancel"
            />

            <CbButton
              styleName="primary"
              onClick={handleSubmit}
              style={{ marginLeft: '0.75rem' }}
              buttonText="Next"
            />
          </Box>
        </Container>
      </MuiCard>
    </>
  );
};

const Container = styled(Box)(() => ({
  width: '70%',
}));

// @ts-expect-error TS(2339): Property 'isBold' does not exist on type 'MUIStyle... Remove this comment to see the full error message
const HeaderTitle = styled('h1')(({ isBold = true, theme }) => ({
  margin: 0,

  fontSize: theme.config.textSizes.canon,
  lineHeight: 1,
  color: `${theme.palette.primary.contrastText} !important`,
  // @ts-expect-error: FIXME
  fontWeight: isBold ? theme.config.weights.bold : theme.config.weights.regular,
}));

// @ts-expect-error TS(2339): Property 'fontSize' does not exist on type 'MUISty... Remove this comment to see the full error message
const Text = styled('p')(({ fontSize, theme }) => ({
  color: theme.palette.primary.contrastText,
  fontSize: fontSize
    ?
      theme.config.textSizes[fontSize]
    : theme.config.textSizes.paragon,
  fontStyle: 'italic',
}));

const TableText = styled(Text)(({ theme }) => ({
  margin: 0,
  lineHeight: 1,
  fontStyle: 'normal',
  color: theme.palette.primary.contrastText,
}));

const Row = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.apiKeys.row,
  height: '2.75rem',
  display: 'flex',
  alignItems: 'center',
  padding: '0 1rem',
  margin: '0.55rem 0',
}));

const HeaderRow = styled(Row)(() => ({
  backgroundColor: 'transparent',
  paddingRight: 0,
}));

const Anchor = styled('span')(({ theme }) => ({
  color: theme.config.colors.cowbellBlue,
  fontWeight: 'bold',
  '&:hover': {
    cursor: 'pointer',
  },
}));

const MuiBackdrop = styled(Backdrop)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  // @ts-expect-error TS(2339): Property 'contrast' does not exist on type 'Palett... Remove this comment to see the full error message
  color: theme.palette.primary.contrast,
}));

const Header = styled('h2')(({ theme }) => ({
  position: 'absolute',
  top: '20%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  textAlign: 'center',
  // @ts-expect-error TS(2339): Property 'contrast' does not exist on type 'Palett... Remove this comment to see the full error message
  color: theme.palette.primary.contrast,

  fontSize: theme.config.textSizes.canon,
}));

const LoadingRenewalSummary = () => {
  return (
    <>
      <MuiCard style={{ height: '100%' }}>
        <Box p={3} height="100%">
          <Skeleton />
        </Box>
      </MuiCard>
      <MuiBackdrop open>
        <Header>We are working on getting your renewal summary.</Header>
        <CircularProgress color="inherit" />
      </MuiBackdrop>
    </>
  );
};
